import _ from 'lodash'
import Vue from 'vue'
import Router, {Route, NavigationGuard, RouteConfig} from 'vue-router'
import store, {loadModule, ValidModuleName} from '@/store'
// Componentes de las rutas iniciales, son cargados en todas las aplicaciones independientemente de
// los modules habilitados para el usuario
import { authGuard } from '@/modules/auth'
import {Colors, notify} from '@/modules/core/notification';

Vue.use(Router);

/**
 * Rutas iniciales
 *
 * Provee las rutas que serán cargadas al inicio de la aplicación, el resto de
 * rutas serán cargadas una vez iniciada sesión según los módulos habilitados
 * por el servidor
 */
const initialRoutes = (): RouteConfig[] => {
    return [
        {
            name: 'login',
            path: '/login',
            component: () => import('@/modules/auth/components/Login.vue'),
            meta: {
                toolbarTitle: 'Iniciar Sesión',
            },
        },
        {
            name: 'dashboard',
            path: '/',
            component: () => import('@/components/Dashboard.vue'),
            meta: {
                toolbarTitle: 'Inicio',
                authRequired: true,
            },
            beforeEnter: async (to, from, next)  => {
                await loadModule('widget')
                next()
            },
        },
        {
            name: 'image-editor',
            path: '/image-editor',
            component: () => import('@/components/ImageEditor.vue'),
            /**
             * Guard que impide acceder al componente si no se accede a el desde la librería multimedia
             *
             * @param to
             * @param from
             * @param next
             */
            beforeEnter: (to, from, next) => {
                if (!from.name || (from.name !== 'media.library.viewer' && from.name !== to.name)) {
                    notify(
                        'El editor de imágenes solo esta disponible a través de la librería multimedia',
                        Colors.Warning,
                        {
                            notShowInCenter: true,
                        },
                    )

                    return next(false)
                }

                next()
            },
            meta: {
                toolbarTitle: 'Editar imagen',
            },
        },
        {
            path: '/dev/wamp',
            component: () => import('@/components/dev/WampManager.vue'),
            meta: {
                toolbarTitle: 'WAMP',
            },
        },
        {
            path: '*',
            component: () => import('@/components/NotFoundPage.vue'),
        },
    ]
}


/**
 * Guard para modificar el estado de la barra de navegación, en función de la
 * ruta seleccionada
 */
const toolbarGuard: NavigationGuard = (to, from, next) => {
    const title = to.meta.toolbarTitle
    store.commit('setToolbarTitle', title)
    document.title = `${title} - Prologue`

    next()
}

/**
 * Guard que almacena en el localStorage la última ruta navegada
 *
 * @param to
 * @param from
 */
const lastRouteGuard = (to: Route, from: Route) => {
    localStorage.setItem('app.lastState', JSON.stringify({
        toRoute: to.name,
        lastLocation: location,
    }))
}

/**
 * Guard que permite detener la navegación por rutas en función de un parámetro
 * del store de la aplicación
 */
const stopNavigationGuard: NavigationGuard = (to, from, next) => {
    if (!store.state.stopNavigation) {
        sessionStorage.removeItem('canceledRoute')
        return next()
    }

    sessionStorage.setItem('canceledRoute', JSON.stringify(to))
    return next(false)
}

const vuexGuard: NavigationGuard = async (to, from, next) => {
    const vuex: {loadModules: ValidModuleName[], dispatch: string[]} = _.get(to, 'meta.vuex', {})
    const modules = vuex.loadModules
    // Antes de carga la ruta se registrará los módulos de vuex que la ruta
    // solicita
    if (modules && modules.length) {
        await loadModule(...modules as ValidModuleName[])
    }

    const actions = vuex.dispatch
    if (actions && actions.length) {
        await Promise.all(actions.map(action => store.dispatch(action)))
    }

    next()
}

let router: Router

/**
 * Factoría del enrutador
 *
 * @returns {VueRouter}
 */
const createRouter: () => Router = () => {
    if (router) {
        return router
    }

    router = new Router({ routes: initialRoutes() })

    // Registra el guardia de navegación para la autorización del usuario
    router.beforeEach(authGuard)
    router.beforeEach(stopNavigationGuard)
    router.beforeEach(toolbarGuard)
    router.beforeEach(vuexGuard)
    router.afterEach(lastRouteGuard)

    return router
}

const loadedRoutes: RouteConfig[] = []

/**
 * Añade una lista de rutas al enrutador, si ya se encuentran registradas no
 * volverá a incluirlas
 *
 * @param routes
 */
export function addRoutes (routes: RouteConfig[]): void {
    for (const route of routes) {
        const index = loadedRoutes.findIndex(r => r.name === route.name)
        if (index !== -1) {
            return
        }

        loadedRoutes.push(route)
        router.addRoutes([route])
    }
}


router = createRouter()

export default router
